import { db } from '../config/firebase';
import { 
  collection, 
  addDoc, 
  doc, 
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import NotificationSettingsService from './NotificationSettingsService';

class ConversationService {
  static async sendMessage({ conversationId, senderId, senderName, senderPhoto, receiverId, message, productId }) {
    try {
      // Validate required parameters
      if (!senderId || !receiverId || !message || !productId) {
        throw new Error('Missing required parameters for sending message');
      }

      // Check notification settings FIRST
      const settingsDoc = await getDoc(doc(db, 'userSettings', receiverId));
      const settings = settingsDoc.exists() ? settingsDoc.data() : null;
      
      // Strict checking of notification settings
      const notificationsEnabled = settings?.notifications?.enabled === true;
      const messagesEnabled = settings?.notifications?.messages === true;
      const shouldCreateNotification = notificationsEnabled && messagesEnabled;

      console.log('Notification settings check:', {
        receiverId,
        notificationsEnabled,
        messagesEnabled,
        shouldCreateNotification,
        rawSettings: settings?.notifications
      });

      const batch = writeBatch(db);

      // Create message
      const messageRef = doc(collection(db, 'messages'));
      batch.set(messageRef, {
        conversationId,
        senderId,
        senderName,
        senderPhoto,
        receiverId,
        message,
        productId,
        read: false,
        createdAt: serverTimestamp(),
      });

      // Update conversation
      batch.update(doc(db, 'conversations', conversationId), {
        lastMessage: message,
        lastMessageTime: serverTimestamp(),
        lastSenderName: senderName,
        lastSenderPhoto: senderPhoto,
        updatedAt: serverTimestamp(),
      });

      // Only create notification if both settings are explicitly true
      if (shouldCreateNotification) {
        console.log('Creating notification - both settings are true');
        const notificationRef = doc(collection(db, 'notifications'));
        batch.set(notificationRef, {
          type: 'message',
          receiverId,
          senderId,
          senderName,
          senderPhoto,
          title: 'New Message',
          message,
          conversationId,
          productId,
          read: false,
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp()
        });
      } else {
        console.log('Skipping notification - settings disabled:', {
          notificationsEnabled,
          messagesEnabled,
          reason: !settingsDoc.exists() ? 'No settings document' : 'Settings disabled'
        });
      }

      await batch.commit();
      return { messageId: messageRef.id };
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  static async createConversation({ participants, productId, lastMessage, lastMessageTime, lastSenderName, lastSenderPhoto }) {
    try {
      // Check if conversation already exists
      const conversationsRef = collection(db, 'conversations');
      const q = query(
        conversationsRef,
        where('participants', 'array-contains', participants[0]),
        where('productId', '==', productId)
      );
      
      const querySnapshot = await getDocs(q);
      const existingConversation = querySnapshot.docs.find(doc => 
        doc.data().participants.includes(participants[1])
      );

      if (existingConversation) {
        return {
          id: existingConversation.id,
          ...existingConversation.data()
        };
      }

      // Create new conversation
      const conversationData = {
        participants,
        productId,
        lastMessage,
        lastMessageTime,
        lastSenderName,
        lastSenderPhoto,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const conversationRef = await addDoc(conversationsRef, conversationData);
      
      return {
        id: conversationRef.id,
        ...conversationData
      };
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }

  static async getMessages(conversationId) {
    try {
      const messagesRef = collection(db, 'messages');
      const q = query(
        messagesRef,
        where('conversationId', '==', conversationId),
        orderBy('createdAt', 'asc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting messages:', error);
      throw error;
    }
  }

  static async getUserConversations(userId) {
    try {
      const conversationsRef = collection(db, 'conversations');
      const q = query(
        conversationsRef,
        where('participants', 'array-contains', userId),
        orderBy('updatedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting conversations:', error);
      throw error;
    }
  }
}

export default ConversationService; 